<template>
  <div class="data-record">
    <div v-if="!isMobileView" class="team row row--hover box box--default">
      <div class="row__data row__data--small row__data--image team__logo">
        <img v-if="userThumbnail" :src="userThumbnail" alt="" />
        <div v-else class="thumbnail thumbnail--team" :style="color">
          {{ parseInitials(tax.team.name) }}
        </div>
      </div>
      <div class="row__data row__data--large row__data--name">
        {{ tax.team.name }}
      </div>

      <div class="row__data row__data--large row__data--address">
        {{ tax.group_name }}
      </div>
      <div class="row__data row__data--large row__data--address">
        {{ tax.name }}
      </div>
      <div class="row__data row__data--medium row__data--rate">
        {{ rateReadable }}
      </div>

      <div class="row__data row__data--action row__data--small" @click.self="showActionsDropdown = !showActionsDropdown"
        v-if="canEdit || canDelete">
        <RowActionDropdown v-if="showActionsDropdown" @toggleDropdown="showActionsDropdown = $event"
          :onEdit="canEdit ? editTax : null" :onDelete="canDelete ? deleteTax : null" />
      </div>
    </div>

    <article v-if="isMobileView" class="data-card box box--default">
      <header class="data-card__header">
        <button type="button" class="data-card__identity">
          <span class="data-card__avatar">
            <img v-if="userThumbnail" :src="userThumbnail" :alt="tax.team.name" />
            <span v-else class="thumbnail thumbnail--team" :style="color">
              {{ parseInitials(tax.team.name) }}
            </span>
          </span>
          <span class="data-card__name">{{ tax.team.name }}</span>
        </button>

        <div class="data-card__action row__data--action" role="button" tabindex="0"
          :aria-label="`${$t('buttons.editBtn')} / ${$t('buttons.deleteBtn')}`"
          @click.self="showCardActionsDropdown = !showCardActionsDropdown"
          @keydown.enter.self="showCardActionsDropdown = !showCardActionsDropdown"
          @keydown.space.prevent.self="showCardActionsDropdown = !showCardActionsDropdown"
          v-if="canEdit || canDelete">
          <RowActionDropdown v-if="showCardActionsDropdown" @toggleDropdown="showCardActionsDropdown = $event"
            :onEdit="canEdit ? editTax : null" :onDelete="canDelete ? deleteTax : null" />
        </div>
      </header>

      <dl v-if="tax" class="data-card__details">
        <div class="data-card__detail">
          <dt>{{ $t(`teamTaxes.${"tableHeader"}.${"tax_group"}`) }}</dt>
          <dd>{{ tax.group_name || '-' }}</dd>
        </div>
        <div class="data-card__detail">
          <dt>{{ $t(`teamTaxes.${"tableHeader"}.${"name"}`) }}</dt>
          <dd>{{ tax.name || '-' }}</dd>
        </div>
        <div class="data-card__detail">
          <dt>{{ $t(`teamTaxes.${"tableHeader"}.${"rate"}`) }}</dt>
          <dd>{{ rateReadable }}</dd>
        </div>
      </dl>
    </article>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from "vue";
import { useStore } from "vuex";
import RowActionDropdown from "@/components/RowActionDropdown";
import { parseInitials } from "@/helpers/entityParsers";

defineOptions({
  name: "TeamTax"
});

const props = defineProps({
  tax: {
    required: true,
    type: Object,
  },
});

const store = useStore();

// State
const showActionsDropdown = ref(false);
const showCardActionsDropdown = ref(false);
const isMobileView = ref(false);

const updateMobileView = () => {
  isMobileView.value = window.innerWidth <= 900;
};

onMounted(() => {
  updateMobileView();
  window.addEventListener("resize", updateMobileView);
});

onBeforeUnmount(() => {
  window.removeEventListener("resize", updateMobileView);
});

// Computed
const userThumbnail = computed(() =>
  props.tax.team.photo ? props.tax.team.photo.thumbnail : null
);

const color = computed(() =>
  props.tax.team.color
    ? { background: props.tax.team.color }
    : { background: "#7f8fa4" }
);

const rateReadable = computed(() =>
  (props.tax.rate * 100).toFixed(2) + " %"
);
const isSuperAdmin = computed(() => store.getters.isSuperAdmin);
const taxTeamId = computed(() => Number(props.tax.team_id || props.tax.team?.id) || null);
const canEdit = computed(() => {
  if (isSuperAdmin.value) return true;
  return store.getters.hasTeamPermission("tax_edit", taxTeamId.value);
});
const canDelete = computed(() => {
  if (isSuperAdmin.value) return true;
  return store.getters.hasTeamPermission("tax_delete", taxTeamId.value);
});

// Methods
const editTax = () => {
  store.commit("setTeamTaxToEdit", props.tax);
  store.commit("setTeamTaxIsEdit", true);
  store.commit("setShowAddTeamTaxModal", true);
};

const deleteTax = () => {
  store.commit("setDeleteModal", {
    display: true,
    message: `'${props.tax.name || props.tax.group_name || props.tax.team?.name || "tax"}'`,
    action: (id) => store.dispatch("deleteTax", { id, teamId: props.tax.team?.id }),
    id: props.tax.id,
  });
};
</script>

<style scoped lang="scss">
.row__data--action {
  max-width: 70px;
}

.team__logo {
  max-width: 70px;
}
</style>
